import React, { useEffect, useState } from 'react';
import { z } from 'zod';

import { trackWithFingerprint } from '../../utils/trackWithFingerprint';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { ImageData } from '../../utils/contentful';
import { TextInput } from '../FormFields/TextInput';
import { DateInput } from '../FormFields/Date';
import { MultiSelect } from '../FormFields/MultiSelect';
import { TextInputType } from '../FormFields/shared';

import { useUserContext } from '~/context/User';
import { usaStateOptions } from '../../data/usaStateOptions';
import Image from 'next/legacy/image';
import closeIcon from '@/images/close-icon.svg';
import { getPartnerTrackingProperties } from '~/utils/getPartnerTrackingProperties';

const sessionFormSchema = z.object({
  eventDate: z.string().min(1, 'Valid event date is required').trim(),
  eventDescription: z
    .string()
    .min(1, 'Event/Group description is required')
    .trim(),
  lessonTopic: z.string().min(1, 'Lesson topic is required').trim(),
  lessonDuration: z.string().min(1, 'Lesson duration is required').trim(),
  numberOfParticipants: z
    .string()
    .min(1, 'Number of participants is required')
    .trim(),
  locationName: z.string().min(1, 'Location name is required').trim(),
  streetAddress: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().min(1, 'ZIP code is required').trim()
});

export type SessionFormData = z.infer<typeof sessionFormSchema>;

interface CustomLink {
  title: string;
  href: string;
}
export interface SubmitSessionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  partnerLogo: ImageData | undefined;
  customLinks?: CustomLink[];
  partnerDisplayName?: string;
}

const INITIAL_FORM_STATE: SessionFormData = {
  eventDate: '',
  eventDescription: '',
  lessonTopic: '',
  lessonDuration: '',
  numberOfParticipants: '',
  locationName: '',
  streetAddress: '',
  city: '',
  state: '',
  zipCode: ''
};

export function SubmitSessionDetailsModal({
  isOpen,
  onClose,
  partnerLogo,
  customLinks,
  partnerDisplayName
}: SubmitSessionDetailsModalProps) {
  const [formState, setFormState] =
    useState<SessionFormData>(INITIAL_FORM_STATE);

  const [formErrors, setFormErrors] = useState<Partial<SessionFormData>>({});
  const { educator } = useUserContext();
  const privacyStatement = 'https://www.glce.org/privacy';
  const termsOfUse = 'https://greenlight.com/classrooms/terms';

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }

    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isOpen]);

  const validateForm = (): boolean => {
    try {
      sessionFormSchema.parse(formState);
      setFormErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Partial<SessionFormData> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            const field = err.path[0] as keyof SessionFormData;
            errors[field] = err.message;
          }
        });
        setFormErrors(errors);
      }
      return false;
    }
  };

  const handleFieldChange = (
    field: keyof SessionFormData,
    value: string | string[]
  ) => {
    const finalValue = Array.isArray(value)
      ? value.length > 0
        ? value[0]
        : ''
      : value;
    setFormState((prev) => ({ ...prev, [field]: finalValue }));

    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleInputChange =
    (field: keyof SessionFormData) =>
    (e: React.ChangeEvent<any>, value: string) =>
      handleFieldChange(field, value);

  const handleSelectChange =
    (field: keyof SessionFormData) => (newValues: string[]) =>
      handleFieldChange(field, newValues);

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    await trackWithFingerprint(
      ANALYTICS_EVENTS.GLCE_WORKSHOP_DETAILS_SUBMITTED,
      {
        ...educator,
        event_date: formState.eventDate,
        event_description: formState.eventDescription,
        lesson_topic: formState.lessonTopic,
        lesson_duration: formState.lessonDuration,
        participant_count: formState.numberOfParticipants,
        location_name: formState.locationName,
        street_address: formState.streetAddress,
        city: formState.city,
        state: formState.state,
        postal_code: formState.zipCode,
        email_opted_in: educator?.reminder === '1',
        custom_links: customLinks?.map((link) => ({
          partner_custom_link_url: link.href,
          partner_custom_link_text: link.title
        })),
        ...getPartnerTrackingProperties()
      }
    );
    handleClose();
  };

  const handleClose = () => {
    setFormState(INITIAL_FORM_STATE);
    setFormErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 top-0 z-[999] flex items-start justify-center overflow-y-auto overflow-x-hidden bg-black/50 px-4 py-[2vh]">
      <div className="m-auto flex h-fit w-[90%] max-w-[736px] flex-col items-center rounded-card-shape bg-white p-6 shadow-[0_2px_6px_0_rgba(0,0,0,0.1),0_0px_2px_0_rgba(0,0,0,0.08),0_0px_0px_1px_rgba(0,0,0,0.2)] mobile:w-[95%] mobile:p-8 tablet:w-[85%] tablet:p-10 desktop:w-full">
        <div className="mb-8 flex w-full flex-col items-center ">
          <div className="relative mb-9 flex w-full gap-40">
            <button
              className="absolute left-0 top-0 cursor-pointer"
              onClick={handleClose}
            >
              <Image
                src={closeIcon}
                alt="Close"
                height={18}
                width={18}
                className="cursor-pointer"
              />
            </button>
            {partnerLogo && (
              <div className="mx-auto mb-6 h-6 max-h-6">
                <Image
                  src={partnerLogo.src}
                  alt={partnerLogo.alt}
                  height={48}
                  width={320}
                  unoptimized={true}
                  style={{ objectFit: 'contain' }}
                />
              </div>
            )}
          </div>

          <p
            className="typography-title-3 max-w-[37.5rem]"
            data-testid="description"
          >
            When you conduct a financial literacy session, please fill out the
            form below. We&apos;ll share these details with {partnerDisplayName}{' '}
            and send you an email at the end of the month with details of any
            sessions you&apos;ve conducted during that month.
          </p>
        </div>

        <div className="w-full max-w-[75%]">
          <h3 className="typography-title-3 mb-2">Basic Details</h3>

          <DateInput
            required
            type={TextInputType.DATE}
            label="Event Date"
            value={formState.eventDate ?? ''}
            error={formErrors.eventDate}
            handleOnChange={handleInputChange('eventDate')}
          />

          <TextInput
            required
            type={TextInputType.TEXT}
            label="Event / Group Description"
            value={formState.eventDescription ?? ''}
            error={formErrors.eventDescription}
            handleOnChange={handleInputChange('eventDescription')}
          />

          <TextInput
            required
            type={TextInputType.TEXT}
            label="Lesson Topic"
            value={formState.lessonTopic ?? ''}
            error={formErrors.lessonTopic}
            handleOnChange={handleInputChange('lessonTopic')}
          />

          <TextInput
            required
            type={TextInputType.NUMBER}
            label="Lesson Duration (minutes, estimated)"
            value={formState.lessonDuration ?? ''}
            error={formErrors.lessonDuration}
            handleOnChange={handleInputChange('lessonDuration')}
          />

          <TextInput
            required
            type={TextInputType.NUMBER}
            label="Number of Participants (estimated)"
            value={formState.numberOfParticipants ?? ''}
            error={formErrors.numberOfParticipants}
            handleOnChange={handleInputChange('numberOfParticipants')}
          />

          <h3 className="typography-title-3 mb-2 mt-8">Session Venue</h3>

          <TextInput
            required
            type={TextInputType.TEXT}
            label="Location Name"
            value={formState.locationName ?? ''}
            error={formErrors.locationName}
            handleOnChange={handleInputChange('locationName')}
          />

          <TextInput
            type={TextInputType.TEXT}
            label="Street Address"
            value={formState.streetAddress ?? ''}
            error={formErrors.streetAddress}
            handleOnChange={handleInputChange('streetAddress')}
          />

          <TextInput
            type={TextInputType.TEXT}
            label="City"
            value={formState.city ?? ''}
            error={formErrors.city}
            handleOnChange={handleInputChange('city')}
          />

          <MultiSelect
            type={TextInputType.TEXT}
            label="State"
            handleChange={handleSelectChange('state')}
            options={usaStateOptions}
            value={
              formState.state && formState.state !== '' ? [formState.state] : []
            }
            error={formErrors.state}
            maxSelection={1}
            variant="preferences"
          />

          <TextInput
            required
            type={TextInputType.TEXT}
            label="ZIP Code"
            value={formState.zipCode ?? ''}
            error={formErrors.zipCode}
            handleOnChange={handleInputChange('zipCode')}
          />
        </div>

        <div className="mt-8 flex w-full max-w-[75%] flex-col items-center">
          <button
            className="mb-6 h-16 w-full rounded-2xl bg-ginger font-medium text-black transition-colors hover:bg-green disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleSubmit}
            data-testid="submit-session-form"
          >
            Submit
          </button>
        </div>

        <p className="typography-body-2 desktop:typography-body-3 text-center text-dark-gray">
          By submitting this information, you consent to our&nbsp;
          <a
            href={privacyStatement}
            target="_blank"
            rel="noopener noreferrer"
            className="text-navy underline transition-colors duration-200 ease-in-out desktop:hover:text-green"
          >
            Privacy Statement
          </a>{' '}
          and{' '}
          <a
            href={termsOfUse}
            target="_blank"
            rel="noopener noreferrer"
            className="text-navy underline transition-colors duration-200 ease-in-out desktop:hover:text-green"
          >
            Terms of Use.
          </a>
        </p>
      </div>
    </div>
  );
}
